import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { AppCard } from '../../components/ui/AppCard';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { fetchMyStats, fetchSystemStats, type MyStats, type SystemStats } from '../../api/dataApi';

// Aggregate stats (Phase 4) - the caller's own footprint (GET /stats/me)
// and the platform-wide roll-up (GET /stats/system). Both are computed on
// read server-side; this screen just fetches once on mount.
export function StatsScreen() {
  const [mine, setMine] = useState<MyStats | null>(null);
  const [system, setSystem] = useState<SystemStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyStats().then((s) => !cancelled && setMine(s));
    fetchSystemStats().then((s) => !cancelled && setSystem(s));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <ScreenHeader title="Stats" />

      <FormSection title="Your footprint">
        <StatRow label="Channels you own" value={mine?.ownedChannels} />
        <StatRow label="Channels you're in" value={mine?.memberChannels} />
        <StatRow label="Schemas you own" value={mine?.ownedTemplates} />
        <StatRow label="Signals across your channels" value={mine?.signalsInOwnedChannels} />
        {mine && mine.monthlySignalLimit !== 0 && (
          <div className="flex items-center justify-between">
            <span className="text-[15px] font-medium">Stored signals this month</span>
            <span className="text-sm text-black/50 dark:text-white/50 tabular-nums">
              {mine.monthlySignalsUsed.toLocaleString()}
              {mine.monthlySignalLimit > 0 ? ` / ${mine.monthlySignalLimit.toLocaleString()}` : ''}
            </span>
          </div>
        )}
      </FormSection>

      <FormSection title="Platform">
        <StatRow label="Channels" value={system?.channels} />
        <StatRow label="Signals" value={system?.signals} />
        <StatRow label="Schemas" value={system?.templates} />
        <StatRow label="Accounts" value={system?.users} />
      </FormSection>

      <Link to="/settings/stats/database">
        <AppCard className="flex items-center gap-3">
          <span className="flex-1 text-[15px] font-medium">Database</span>
          <ChevronRight size={18} className="text-black/30 dark:text-white/30" />
        </AppCard>
      </Link>

      <p className="text-xs text-black/40 dark:text-white/40 px-1">
        Platform totals are also posted as a daily snapshot into the public <span className="font-medium">@zmist-stats</span> channel — follow it
        for the feed.
      </p>
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[15px] font-medium">{label}</span>
      <span className="text-sm text-black/50 dark:text-white/50 tabular-nums">
        {value === undefined ? '…' : value.toLocaleString()}
      </span>
    </div>
  );
}
